import React, { useEffect, useState } from "react";
import PageHero from "../components/PageHero";
import { api } from "../lib/api";
import { Calendar, Megaphone, FileText, Download } from "lucide-react";
import { Badge } from "../components/ui/badge";

export function Announcements() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/announcements?limit=50").then(({ data }) => setItems(data.items)).catch(() => {}); }, []);
  return (
    <div data-testid="announcements-page">
      <PageHero eyebrow="Pengumuman" title="Pengumuman Resmi" subtitle="Informasi penting dari sekolah untuk murid, orang tua, dan masyarakat." />
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-4">
          {items.map((a) => (
            <div key={a.id} data-testid={`announcement-${a.id}`} className="card-elevated p-6 flex gap-5">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0"><Megaphone /></div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Badge className={a.status === "active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>{a.status === "active" ? "Aktif" : "Arsip"}</Badge>
                  <span className="text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString("id-ID")}</span>
                </div>
                <h3 className="font-serif font-bold text-xl text-[#0B1D3A] mt-2">{a.title}</h3>
                <p className="mt-2 text-slate-600">{a.content}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function CalendarPage() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/events?limit=100").then(({ data }) => setItems(data.items)).catch(() => {}); }, []);
  const sorted = [...items].sort((a, b) => a.start_date.localeCompare(b.start_date));
  return (
    <div data-testid="calendar-page">
      <PageHero eyebrow="Kalender" title="Kalender Sekolah" subtitle="Jadwal akademik, ujian, libur, dan kegiatan sekolah untuk tahun ajaran berjalan." />
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-4">
          {sorted.map((e) => (
            <div key={e.id} data-testid={`event-${e.id}`} className="card-elevated p-6 flex gap-5">
              <div className="w-16 flex-shrink-0 text-center">
                <div className="text-xs uppercase font-bold text-amber-600">{new Date(e.start_date).toLocaleDateString("id-ID", { month: "short" })}</div>
                <div className="font-serif text-3xl font-black text-[#0B1D3A]">{new Date(e.start_date).getDate()}</div>
              </div>
              <div className="flex-1 border-l border-slate-200 pl-5">
                <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[11px] uppercase tracking-wider font-bold">{e.category}</Badge>
                <h3 className="font-serif font-bold text-lg text-[#0B1D3A] mt-2">{e.title}</h3>
                <p className="text-sm text-slate-600 mt-1">{e.description}</p>
                <div className="mt-2 text-xs text-slate-500">
                  {new Date(e.start_date).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
                  {e.end_date && ` — ${new Date(e.end_date).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}`}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function Downloads() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/documents?limit=100").then(({ data }) => setItems(data.items)).catch(() => {}); }, []);
  return (
    <div data-testid="downloads-page">
      <PageHero eyebrow="Pusat Unduhan" title="Download Center" subtitle="Formulir, panduan, kalender pendidikan, brosur, dan dokumen penting sekolah." />
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-5">
          {items.map((d) => (
            <div key={d.id} data-testid={`doc-${d.id}`} className="card-elevated p-6 flex items-center gap-5">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0"><FileText /></div>
              <div className="flex-1">
                <Badge className="bg-slate-100 text-slate-700 text-[11px] uppercase tracking-wider font-bold">{d.category}</Badge>
                <h3 className="font-serif font-bold text-lg text-[#0B1D3A] mt-2">{d.title}</h3>
                <p className="text-xs text-slate-500 mt-1">{d.description}</p>
              </div>
              <a href={d.file_url} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-[#0B1D3A] text-white hover:bg-amber-500 hover:text-[#0B1D3A] flex items-center justify-center flex-shrink-0 transition-colors"><Download className="w-4 h-4" /></a>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function PortalPlaceholder({ title }) {
  return (
    <div data-testid="portal-placeholder" className="pt-32 pb-20">
      <PageHero eyebrow="Portal" title={title} subtitle="Portal ini sedang dalam pengembangan. Fitur lengkap akan segera tersedia." />
      <section className="py-20 bg-white text-center">
        <div className="max-w-xl mx-auto px-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-amber-50 text-amber-600 mb-6"><Megaphone className="w-9 h-9" /></div>
          <h2 className="font-serif text-2xl font-bold text-[#0B1D3A]">Segera Hadir</h2>
          <p className="mt-3 text-slate-600">Portal {title} akan memungkinkan Anda mengakses jadwal, nilai, materi, dan komunikasi sekolah. Nantikan peluncurannya!</p>
        </div>
      </section>
    </div>
  );
}
